<template>
    <div
        v-if="show"
        class="reg-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="regTitle"
        @click.self="close"
        @keydown.esc="close"
    >
        <div class="reg-box">
            <!-- Title bar -->
            <div class="title-bar">
                <button class="btn-close" @click="close" aria-label="Close"></button>
                <h5 id="regTitle" class="m-0">Earn with Allmyonline</h5>
            </div>

            <!-- Hero card (like the screenshot) -->
            <div class="hero">
                <div class="hero-copy">
                    <h2 class="headline">
                        Join allmyonline and earn 25%
                    </h2>
                    <p class="sub">
                        One link to share everything you create, curate and sell across IG,
                        TikTok and more.
                    </p>

                    <!-- Username input like "allmy.online/ yourname" -->
                    <form class="claim" @submit.prevent="submit">
                        <div class="input-wrap">
                            <span class="prefix">{{ prefix }}</span>
                            <input
                                v-model.trim="username"
                                type="text"
                                class="input"
                                placeholder="yourname"
                                autocomplete="username"
                                required
                            />
                        </div>
                        <button class="cta" type="submit" :disabled="!username">
                            Claim your Allmyonline
                        </button>
                    </form>

                    <div class="links">
                        <a :href="urlName" target="_blank">Explore more profiles</a>
                        <a :href="urlName" target="_blank" >Learn more about Allmyonline</a>
                    </div>

                    <button class="cta-outline" @click="goSignup">Sign up free</button>
                </div>

                <!-- Right side tilted mock card -->
                <div class="hero-art" aria-hidden="true">
                    <div class="phone">
                        <div class="screen"></div>
                    </div>
                </div>
            </div>

            <p class="fine-print">
                By continuing, you agree to our Terms and Privacy Policy.
            </p>
        </div>
    </div>
</template>

<script>
export default {
    name: "RegisterHeroPopup",
    props: {
        show: { type: Boolean, default: false },
        urlName: { type: String, default: "https://allmy.online/" },
        referral_code: { type: String, default: null },
        prefix: { type: String, default: "allmy.online/" }, // shown before the username
    },
    emits: ["close"],
    data() {
        return { username: "" };
    },
    methods: {
        goSignup() {
            const host = ((this.urlName || window.location?.origin || 'http://localhost:8000'))
                .toString()
                .replace(/\/+$/, '');
            const handle = (this.username || '')
                .trim()
                .replace(/\s+/g, '')
                .toLowerCase();

            const refFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('ref') : null;
            const ref = this.referral_code || refFromUrl;

            let url = `${host}/signup/details?username=${encodeURIComponent(handle)}`;
            if (ref) url += `&ref=${encodeURIComponent(ref)}`;

            window.open(url, '_blank', 'noopener');
            this.close();
        },
        submit() {
            this.goSignup();
        },
        learn(which) {
            // replace with real links/routes

            window.open(this.urlName, "_blank");
        },
        close() {
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
/* ---------- Layout ---------- */
.reg-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,.5);
    z-index: 9999;
    display: grid;
    place-items: center;
    padding: 16px;
    overflow-y: auto; /* allow scroll on small screens */
    -webkit-overflow-scrolling: touch; /* smooth iOS scrolling */
}
.reg-box {
    background: var(--background-primary);
    border-radius: 1rem;
    width: 56.25rem;
    max-width: 100%;
    padding: 1.25rem 1.5rem 1rem;
    box-shadow: 0 1.25rem 3.75rem rgba(0,0,0,.2);
    max-height: calc(100vh - 2rem);
    overflow: auto;
}
.title-bar {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 2.5rem;
}
.btn-close {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    appearance: none;
    border: 0;
    background: transparent;
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    cursor: pointer;
    color: var(--text-muted);
    transition: color 0.2s ease;
}
.btn-close:hover {
    color: var(--text-primary);
}
.btn-close::before {
    content: "✕";
    display: block;
    font-size: 1.125rem;
    line-height: 2rem;
}

/* ---------- Hero ---------- */
.hero {
    margin-top: 1rem;
    background: var(--primary-100);
    border-radius: 1.125rem;
    padding: 1.75rem;
    display: grid;
    grid-template-columns: 1.2fr .8fr;
    gap: 1.25rem;
    align-items: center;
}
.headline {
    font-size: clamp(1.75rem, 3.4vw, 2.75rem);
    line-height: 1.05;
    margin: 0 0 0.625rem;
    font-weight: 800;
    letter-spacing: -0.03125rem;
}
.headline .em { font-variant-numeric: tabular-nums; }
.headline .blue { color: var(--primary); }
.sub {
    margin: 0 0 1.125rem;
    font-size: 1rem;
    max-width: 46ch;
}

/* ---------- Input + CTA ---------- */
.claim { display: grid; grid-template-columns: 1fr; gap: 0.75rem; max-width: 32.5rem; }
.input-wrap {
    display: flex;
    align-items: center;
    background: var(--background-primary);
    border-radius: 0.75rem;
    padding: 0.75rem 0.875rem;
    border: 1px solid var(--border-color);
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}
.prefix {
    flex: 0 0 auto;
    font-weight: 600;
    color: var(--text-muted);
    margin-right: 0.375rem;
    white-space: nowrap;
}
.input {
    border: 0;
    outline: none;
    background: transparent;
    flex: 1 1 auto;
    min-width: 0;
    font-size: 1rem;
    color: var(--text-primary);
}

input::placeholder {
    color: var(--text-muted);
    text-align: left;
}

.cta {
    appearance: none;
    border: 0;
    border-radius: 2.25rem;
    padding: 1rem 1.375rem;
    font-weight: 700;
    font-size: 1rem;
    background: var(--primary);
    color: var(--text-on-dark);
    cursor: pointer;
    transition: background 0.2s ease;
}
.cta:hover {
    background: var(--primary-600);
}
.cta:disabled {
    opacity: .6;
    cursor: not-allowed;
}

.links {
    display: grid;
    gap: 0.375rem;
    margin: 1rem 0 0.625rem;
}
.links a {
    color: inherit;
    text-decoration: underline;
}

.cta-outline {
    appearance: none;
    background: transparent;
    border: 2px solid var(--primary);
    color: var(--primary);
    padding: 0.75rem 1.125rem;
    border-radius: 2rem;
    font-weight: 700;
    width: fit-content;
    cursor: pointer;
    transition: all 0.2s ease;
}
.cta-outline:hover {
    background: var(--primary);
    color: var(--text-on-dark);
}

/* ---------- Right art ---------- */
.hero-art {
    display: grid;
    place-items: center;
}
.phone {
    width: min(280px, 70%);
    aspect-ratio: 9/16;
    background: #ff7a00;
    border-radius: 28px;
    transform: rotate(18deg);
    box-shadow: 0 24px 40px rgba(0,0,0,.25);
    position: relative;
}
.phone::after {
    content: "";
    position: absolute;
    inset: 14px;
    border-radius: 22px;
    background: #fff3e6;
}
.screen {
    position: absolute;
    inset: 0.875rem;
    border-radius: 1rem;
    /* Show real screenshot image inside the phone screen */
    background: var(--background-primary) url("/template/allmyonline/assets/demo/demo-riosrenews.jpg") center/cover no-repeat;
    z-index: 1;
}

/* ---------- Footer ---------- */
.fine-print {
    margin: 0.625rem 0.375rem 0;
    font-size: 0.75rem;
    color: var(--text-muted);
    text-align: center;
}

/* ---------- Responsive ---------- */
@media (max-width: 51.25rem) {
    .hero {
        grid-template-columns: 1fr;
        text-align: left;
    }
    .hero-art { order: -1; margin-bottom: 0.75rem; }
}

/* Extra-small phones */
@media (max-width: 36rem) {
    .reg-overlay {
        align-items: stretch;
        padding: 0;
    }
    .reg-box {
        width: 100%;
        max-width: none;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
        padding: 1rem;
        margin: 0;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
    }
    .hero { padding: 1rem; }
    .hero-art { display: none; }
    .headline { font-size: 1.625rem; }
    .claim { width: 100%; max-width: 100%; }
    .input-wrap { width: 100%; max-width: 100%; padding: 0.625rem 0.75rem; }
    .cta { padding: 0.875rem 1.125rem; width: 100%; display: block; }
    .cta-outline { padding: 0.625rem 1rem; }
}
</style>
